/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  CardHeader,
  CardFooter,
  Table,
  Row,
  Button,
  Modal,
  ModalBody,
  Form,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Input,
  Col,
  Container,
} from "reactstrap";
import { useToasts } from "react-toast-notifications";
import ReactPaginate from "react-paginate";

import FullPageLoader from "components/FullPageLoader/fullpageloader";
import NoRecordFound from "components/NoRecordFound/NoRecordFound";
import { btnCss, iconCss } from "../../appConfig";
import { getLoginType, postMethod } from "../../services/httpServices";
import Header from "components/Headers/Header";
import { getAmt } from "services/util";
import AddProduct from "./add-product";
import ViewProduct from "./view-product";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "assets/img/brand/project_logo.png";
import QRCode from "qrcode";
import moment from "moment";

const InventoryProductsList = () => {
  const [loading, setLoading] = useState(false);
  const [posList, setPosList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [view, setView] = useState(false);
  const [POSObj, setPOSObj] = useState(null);
  const [totalRec, setTotalRec] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" | "update"
  const [editProductData, setEditProductData] = useState([]);

  const tiemrRef = useRef();

  const tableRef = useRef(null);
  const { addToast } = useToasts();

  useEffect(() => {
    fetchProducts();
  }, [currentPage, sortConfig]);

  useEffect(() => {
    tiemrRef.current = setTimeout(() => {
      fetchProducts();
    }, 500);

    return () => clearTimeout(tiemrRef.current);
  }, [searchText]);

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const notifyFailed = (text = "Error in saving") => {
    addToast(text, { appearance: "error", autoDismiss: true });
  };

  const reloadList = () => {
    setSearchText("");
    setCurrentPage(0);
    setSortConfig({ key: "", direction: "asc" });
    fetchProducts();
  };

  const showPOS = (obj) => {
    setPOSObj(obj);
    setView(true);
  };

  const closeViewModal = () => {
    setView(false);
    setPOSObj(null);
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      let direction = prev.direction === "asc" ? "desc" : "asc";
      if (prev.key !== key) direction = "asc";
      return { key, direction };
    });
  };

  const fetchProducts = async (perPage = 10) => {
    setLoading(true);
    try {
      const res = await postMethod(
        "/api/Inventory/GetSearchAllProducts",
        {
          searchText: searchText,
          isDescending: sortConfig.direction === "desc",
          sortingOn: sortConfig.key,
          perPage: perPage,
          pageNumber: currentPage + 1,
        },
        "post"
      );
      setLoading(false);
      if (res?.data?.responseCode === "200") {
        const list = res.data.result.list || [];
        setPosList(list);
        setFilteredList(list);
        setTotalRec(res.data.result.totalRecords);
        setPageCount(res.data.result?.totalPages);
        return list;
      } else {
        notifyFailed(res?.data?.responseMessage || "Error in fetching list");
      }
    } catch (err) {
      setLoading(false);
      notifyFailed("Something went wrong!, Please call 3737");
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="fas fa-sort ml-1" />;
    return sortConfig.direction === "asc" ? (
      <i className="fas fa-sort-up ml-1" />
    ) : (
      <i className="fas fa-sort-down ml-1" />
    );
  };

  const generatePDF = async (list) => {
    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const qrSize = 20;
      const currentDate = new Date().toLocaleString();

      // Header background
      doc.setFillColor(41, 128, 185);
      doc.rect(0, 0, pageWidth, 20, "F");

      // Company logo - Adjust 'logo' variable with your base64/png image
      const logoWidth = 15;
      const logoHeight = 10;
      if (typeof logo === "string" && logo) {
        doc.addImage(
          logo,
          "PNG",
          pageWidth - logoWidth - margin,
          5,
          logoWidth,
          logoHeight
        );
      }

      // Header text
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text("Product Inventory List", margin, 12);

      doc.setFontSize(8);
      doc.text(`Generated: ${currentDate}`, margin, 17);

      doc.setTextColor(0, 0, 0);

      const headers = [
        [
          "QR Code",
          "Product Name",
          "Category",
          "Trade Unit",
          "Unit Cost",
          "Unit Price",
          "Created Date",
        ],
      ];

      const rows = await Promise.all(
        list
          .filter((item) => item.productName)
          .map(async (item) => {
            let qrDataUrl = "";
            try {
              qrDataUrl = item.productQRCodebase64
                ? `data:image/png;base64,${item.productQRCodebase64}`
                : await QRCode.toDataURL(item.productName, {
                    width: 300,
                    margin: 1,
                    color: { dark: "#2c3e50", light: "#ffffff" },
                  });
            } catch (err) {
              console.error("QR generation failed for", item.productName, err);
            }

            return [
              { qr: qrDataUrl },
              item.productName,
              item.categoryName,
              item.tradeUnit,
              getAmt(item.unitCost),
              getAmt(item.unitPrice),
              moment(item.createdAt).format("DD/MM:YYYY hh:mm"),
            ];
          })
      );

      if (rows.length === 0) {
        alert("No valid product data found.");
        return;
      }

      doc.autoTable({
        startY: 25,
        head: headers,
        body: rows,
        margin: { left: margin, right: margin },
        tableWidth: "auto",
        styles: {
          fontSize: 9,
          cellPadding: 2,
          valign: "middle", // vertical align center
          overflow: "linebreak",
          minCellHeight: qrSize + 4,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "left",
          minCellHeight: 15, // smaller header height
        },
        columnStyles: {
          0: { cellWidth: qrSize + 10, halign: "center" },
        },
        didDrawCell: (data) => {
          const { column, cell } = data;

          if (column.index === 0 && cell.raw?.qr) {
            doc.addImage(
              cell.raw.qr,
              "PNG",
              cell.x + 5,
              cell.y + (cell.height - qrSize) / 2, // center vertically in cell
              qrSize,
              qrSize
            );
            // Remove default text rendering for this cell (to avoid text fallback)
            data.cell.text = [];
          }
        },
        didDrawPage: (data) => {
          doc.setFontSize(8);
          doc.setTextColor(100);
          doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
            pageWidth - margin,
            pageHeight - 5,
            { align: "right" }
          );
        },
      });

      doc.save("Product_Inventory.pdf");
    } catch (error) {
      console.error("PDF generation error:", error);
      alert("Failed to generate PDF");
    }

    fetchProducts();
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Modal
          isOpen={showAddProductModal}
          style={{ width: "90vw", maxWidth: "90vw" }}
        >
          <ModalBody style={{ padding: 0, width: "100%" }}>
            <AddProduct
              mode={modalMode}
              initialData={editProductData}
              closeModal={() => {
                setShowAddProductModal(false);
                fetchProducts();
              }}
            />
          </ModalBody>
        </Modal>
        <Modal isOpen={view} style={{ width: "70vw", maxWidth: "70vw" }}>
          <ModalBody style={{ padding: 0 }}>
            <ViewProduct closeModal={closeViewModal} product={POSObj} />
          </ModalBody>
        </Modal>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 d-flex p-0 justify-content-between align-items-center">
                <Form
                  className="form-inline mr-3 d-md-flex ml-lg-auto"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <FormGroup className="mb-0 navbar-search ">
                    <InputGroup className="input-group-alternative">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="fas fa-search" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        className="search-cls"
                        placeholder="Search"
                        type="text"
                        value={searchText}
                        onChange={(e) => handleSearchChange(e.target.value)}
                      />
                    </InputGroup>
                  </FormGroup>
                  &nbsp;&nbsp;
                  <Button
                    className="my-4 ml-2"
                    color="danger"
                    onClick={() => {
                      fetchProducts(totalRec).then((res) => {
                        generatePDF(res);
                      });
                    }}
                  >
                    <i className="fas fa-file-pdf mr-1" />
                  </Button>
                  &nbsp;&nbsp;
                  <Button
                    className="my-4"
                    color="primary"
                    onClick={() => {
                      setModalMode("add");
                      setEditProductData([]);
                      setShowAddProductModal(true);
                    }}
                  >
                    Add Product
                  </Button>
                  <Button
                    color="primary"
                    onClick={reloadList}
                    size="sm"
                    style={btnCss}
                    data-toggle="tooltip"
                    title="Refresh"
                  >
                    <i className="fas fa-redo fa-lg" style={iconCss} />
                  </Button>
                </Form>
              </CardHeader>

              <div ref={tableRef}>
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr>
                      {[
                        ["categoryName", "Category Name"],
                        ["productName", "Product Name"],
                        ["tradeUnit", "Trade Unit"],
                        ["unitCost", "Unit Cost (Buying)"],
                        ["unitPrice", "Unit Price (Selling)"],
                        ["createdAt", "Created Date"],
                      ].map(([key, label]) => (
                        <th
                          key={key}
                          onClick={() => toggleSort(key)}
                          style={{ cursor: "pointer" }}
                        >
                          {label} {getSortIcon(key)}
                        </th>
                      ))}
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posList.length > 0 ? (
                      posList
                        .reduce((acc, item, index, array) => {
                          const subItems = array.filter(
                            (sub) => sub.categoryCode === item.categoryCode
                          );

                          if (
                            !acc.some((row) => row.key === item.categoryCode)
                          ) {
                            acc.push({
                              key: item.categoryCode,
                              rows: subItems,
                            });
                          }

                          return acc;
                        }, [])
                        .map((group) =>
                          group.rows.map((item, index) => (
                            <tr key={item.id}>
                              {index === 0 && (
                                <>
                                  <td
                                    rowSpan={group.rows.length}
                                    style={{
                                      verticalAlign: "middle",
                                      backgroundColor: "#f8f9fe",
                                    }}
                                  >
                                    {item.categoryName}
                                  </td>
                                </>
                              )}
                              <td>{item.productName}</td>
                              <td>{item.tradeUnit}</td>
                              <td>{getAmt(item.unitCost)}</td>
                              <td>{getAmt(item.unitPrice)}</td>
                              <td>
                                {moment(item.createdAt).format(
                                  "DD/MM/YYYY hh:mm"
                                )}
                              </td>

                              <td>
                                <Button
                                  color="primary"
                                  onClick={() => showPOS(item)}
                                  size="sm"
                                  style={btnCss}
                                  data-toggle="tooltip"
                                  title="View"
                                >
                                  <i
                                    className="fas fa-eye fa-lg"
                                    style={iconCss}
                                  />
                                </Button>
                                {getLoginType() === "Merchant" && (
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    className="ml-2"
                                    style={btnCss}
                                    onClick={() => {
                                      // Collect all products with same category & subcategory for editing
                                      const selectedCombo = `${item.categoryCode}`;
                                      const groupToEdit = filteredList.filter(
                                        (p) =>
                                          `${p.categoryCode}` === selectedCombo
                                      );
                                      setModalMode("update");
                                      setEditProductData(groupToEdit);
                                      setShowAddProductModal(true);
                                    }}
                                  >
                                    <i
                                      className="fas fa-edit fa-lg"
                                      style={iconCss}
                                    />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))
                        )
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center">
                          No records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {/* <tbody>
                    {posList.map((item, i) => (
                      <tr key={item.id || i}>
                        <td>{item.categoryCode}</td>
                        <td>{item.categoryName}</td>
                      
                      </tr>
                    ))}
                  </tbody> */}
                </Table>
              </div>

              {loading && <FullPageLoader label="Fetching..." />}
              {/* {!loading && !filteredList.length && <NoRecordFound />} */}

              <CardFooter className="py-4">
                <Row>
                  <Col lg="6">
                    <small>
                      <strong>Total Records : {totalRec}</strong>
                    </small>
                  </Col>
                  <Col lg="6">
                    <nav aria-label="...">
                      <div className="float-right">
                        <ReactPaginate
                          previousLabel="prev"
                          nextLabel="next"
                          breakLabel="..."
                          pageCount={pageCount}
                          marginPagesDisplayed={1}
                          pageRangeDisplayed={2}
                          onPageChange={({ selected }) =>
                            setCurrentPage(selected)
                          }
                          containerClassName="paginationV2"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      </div>
                    </nav>
                  </Col>
                </Row>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default InventoryProductsList;
